import { useCallback } from 'react';
import {
  parseAsArrayOf,
  parseAsBoolean,
  parseAsJson,
  parseAsString,
  parseAsStringEnum,
  useQueryStates,
} from 'nuqs';
import {
  DEFAULT_VOL_SKEW_CONFIG,
  GreeksStatsLookback,
  PersistedVolSkewLine,
  StrikeDisplayType,
  VolSkewConfig,
} from 'types/impliedVol';

// ─── URL serialization ───────────────────────────────────────────────────────

function parsePersistedVolSkewLines(value: unknown): PersistedVolSkewLine[] {
  if (
    Array.isArray(value) &&
    value.every(
      (v) =>
        typeof v === 'object' &&
        v != null &&
        typeof (v as PersistedVolSkewLine).key === 'string' &&
        typeof (v as PersistedVolSkewLine).tradeDate === 'string' &&
        typeof (v as PersistedVolSkewLine).expiryDate === 'string',
    )
  ) {
    return value as PersistedVolSkewLine[];
  }
  return [];
}

const VS_QUERY_KEYS = {
  vs_strike: parseAsStringEnum<StrikeDisplayType>(
    Object.values(StrikeDisplayType),
  ).withDefault(DEFAULT_VOL_SKEW_CONFIG.strikeDisplay),
  vs_stats: parseAsBoolean.withDefault(DEFAULT_VOL_SKEW_CONFIG.showStats),
  vs_div: parseAsBoolean.withDefault(DEFAULT_VOL_SKEW_CONFIG.showDividends),
  vs_diff: parseAsBoolean.withDefault(DEFAULT_VOL_SKEW_CONFIG.showSkewDiff),
  vs_brush: parseAsBoolean.withDefault(DEFAULT_VOL_SKEW_CONFIG.showZoomBrush),
  vs_sgiv: parseAsBoolean.withDefault(DEFAULT_VOL_SKEW_CONFIG.useSpotGammaIV),
  vs_lb: parseAsStringEnum<GreeksStatsLookback>(
    Object.values(GreeksStatsLookback),
  ).withDefault(DEFAULT_VOL_SKEW_CONFIG.statsLookback),
  vs_lines: parseAsJson<PersistedVolSkewLine[]>(
    parsePersistedVolSkewLines,
  ).withDefault([]),
  vs_hidden_keys: parseAsArrayOf(parseAsString).withDefault([]),
  // Absent on fresh/legacy URLs → the chart treats it as a new day.
  vs_asof: parseAsString,
};

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useVolSkewConfig(): [
  VolSkewConfig,
  (patch: Partial<VolSkewConfig>) => void,
] {
  const [params, setParams] = useQueryStates(VS_QUERY_KEYS);

  const config: VolSkewConfig = {
    strikeDisplay: params.vs_strike,
    showStats: params.vs_stats,
    showDividends: params.vs_div,
    showSkewDiff: params.vs_diff,
    showZoomBrush: params.vs_brush,
    useSpotGammaIV: params.vs_sgiv,
    statsLookback: params.vs_lb,
    lines: params.vs_lines,
    hiddenKeys: params.vs_hidden_keys,
    asOfDate: params.vs_asof ?? undefined,
    // Caller-supplied per-render, not a user setting - nothing to persist.
    markedStrikes: [],
  };

  const updateConfig = useCallback(
    (patch: Partial<VolSkewConfig>) => {
      const urlPatch: Partial<typeof params> = {};

      if (patch.strikeDisplay != null) {
        urlPatch.vs_strike = patch.strikeDisplay;
      }
      if (patch.showStats != null) {
        urlPatch.vs_stats = patch.showStats;
      }
      if (patch.useSpotGammaIV != null) {
        urlPatch.vs_sgiv = patch.useSpotGammaIV;
      }
      if (patch.showDividends != null) {
        urlPatch.vs_div = patch.showDividends;
      }
      if (patch.showSkewDiff != null) {
        urlPatch.vs_diff = patch.showSkewDiff;
      }
      if (patch.showZoomBrush != null) {
        urlPatch.vs_brush = patch.showZoomBrush;
      }
      if (patch.statsLookback != null) {
        urlPatch.vs_lb = patch.statsLookback;
      }
      if (patch.lines != null) {
        urlPatch.vs_lines = patch.lines;
      }
      if (patch.hiddenKeys != null) {
        urlPatch.vs_hidden_keys = patch.hiddenKeys;
      }
      if ('asOfDate' in patch) {
        urlPatch.vs_asof = patch.asOfDate ?? null;
      }

      void setParams(urlPatch);
    },
    [setParams],
  );

  return [config, updateConfig];
}
